import {activeCityDistrictLayout} from './ssx-city-district-layout.mjs';
/**
 * A remembered city after sunset: human-scale side streets, corner shops,
 * apartment rooftops, utility wires and doorways into connected worlds.
 * Authored native geometry; shared instanced detail, no external assets.
 */
export const CITY_SPAWN = [0, 0, 30];
export const CITY_PORTALS = [
  { id: 'garden', name: 'Moonwater', label: 'Moonwater', targetWorldId: 'garden', position: [-15, 0, 13], color: '#71dace', kind: 'portal', radius: 3.2, width: 3.2, height: 3.4, aperture: 'rectangle', yaw: 0 },
  { id: 'observatory', name: 'Astral', label: 'Astral', targetWorldId: 'observatory', position: [15, 0, 13], color: '#c696df', kind: 'portal', radius: 3.2, width: 3.2, height: 3.4, aperture: 'rectangle', yaw: 0 },
  { id: 'studio', name: 'Your studio', label: 'Your studio', targetWorldId: 'studio', position: [0, 0, 44], color: '#e9b78e', kind: 'portal', radius: 3.2, width: 3.2, height: 3.4, aperture: 'rectangle', yaw: Math.PI },
];
export const CITY_COLLIDERS = [
  { x: -17, z: 4, width: 18, depth: 16, height: 13 },
  { x: 17, z: 4, width: 18, depth: 16, height: 16 },
  { x: -16, z: -18, width: 16, depth: 18, height: 18 },
  { x: 16, z: -18, width: 16, depth: 18, height: 15 },
  { x: -14, z: -42, width: 12, depth: 16, height: 12 },
  { x: 16, z: -42, width: 16, depth: 16, height: 20 },
  { x: -25, z: 32, width: 14, depth: 14, height: 10 },
  { x: 25, z: 32, width: 14, depth: 14, height: 12 },
  { x: -37, z: 2, width: 13, depth: 18, height: 18 },
  { x: 38, z: 2, width: 14, depth: 18, height: 14 },
  { x: -38, z: -24, width: 15, depth: 17, height: 14 },
  { x: 38, z: -24, width: 15, depth: 17, height: 19 },
  { x: -40, z: 31, width: 13, depth: 13, height: 8 },
  { x: 40, z: 32, width: 13, depth: 15, height: 10 },
  { x: 0, z: 53, width: 13, depth: 12, height: 8 },
  ...CITY_PORTALS.flatMap((p) => [-1, 1].map((side) => ({ x: p.position[0] + side * 1.96, z: p.position[2], width: .55, depth: 1.7, height: 3.8 }))),
];

export const hasResortBuildingEntrance=world=>world?.portals?.some(p=>p.id==='mirage-resort'&&p.position.x===17.2&&p.position.z===32&&p.yaw===-Math.PI/2)===true;

export function createCity(THREE, options = {}) {
  const T = THREE;
  const districtLayout=activeCityDistrictLayout(options.world);
  const group = new T.Group();
  group.name = 'Valoria · evening streets';
  const batches = new Map();
  const geometries = new Map();
  const animated = [];
  const shaders = [];
  const ownedTextures = [];
  const portalDefinitions = CITY_PORTALS.map((base) => {
    const connected = options.world?.portals?.find((p) => p.id === base.id || (Math.abs((p.position?.x ?? p.position?.[0] ?? 999) - base.position[0]) < .5 && Math.abs((p.position?.z ?? p.position?.[2] ?? 999) - base.position[2]) < .5));
    return { ...base, name: connected?.label || base.name, targetWorldId: connected?.targetWorldId || base.targetWorldId, active: !options.world || Boolean(connected) };
  });
  const materials = {};
  const matrixObject = new T.Object3D();
  const palette = {
    stone: '#9c939a', ivory: '#c7bcb6', pale: '#615c6d', rose: '#735b70',
    dark: '#242532', glass: '#263641', teal: '#456a70', copper: '#686a73',
    foliage: '#4a6c64', foliageLight: '#7f9480', blossom: '#987d9e',
    cyan: '#65e0e0', violet: '#d074df', gold: '#ffd19b',
  };
  const mat = (name, color, extra = {}) => {
    const m = new T.MeshStandardMaterial({ color, roughness: 0.74, metalness: 0.08, ...extra });
    m.name = name; materials[name] = m; return m;
  };
  const m = {
    stone: mat('cool architectural concrete', palette.stone, { roughness: 0.62 }),
    ivory: mat('aged pale facade', palette.ivory, { roughness: 0.82, metalness: 0.02 }),
    pale: mat('graphite violet paving', palette.pale),
    paving: mat('pale titanium promenade', '#746e80', { roughness: 0.83, metalness: 0.1 }),
    rose: mat('dusty plum render', palette.rose, { roughness: 0.82, metalness: 0.04 }),
    dark: mat('deep slate', palette.dark, { roughness: 0.8 }),
    glass: mat('evening shop glass', palette.glass, { roughness: 0.28, metalness: 0.35 }),
    teal: mat('blue titanium', palette.teal, { metalness: 0.56, roughness: 0.3 }),
    copper: mat('brushed dark steel', palette.copper, { metalness: 0.72, roughness: 0.3 }),
    wood: mat('dark painted timber', '#51464f'),
    leaf: mat('sage foliage', palette.foliage),
    leafLight: mat('sunlit foliage', palette.foliageLight),
    blossom: mat('ultraviolet canopy', palette.blossom),
    soil: mat('planter soil', '#363842'),
    lawn: mat('velvet garden', '#344f55'),
    roadway: mat('graphite outer promenade', '#373847'),
    sand: mat('city foundation', '#4a4156'),
    window: mat('twilight window', '#40758b', { roughness: 0.2, metalness: 0.5, emissive: '#3ebfe3', emissiveIntensity: 0.16 }),
    warmWindow: mat('warm interior', '#c2a190', { roughness: 0.35, emissive: '#ffb58b', emissiveIntensity: 0.27 }),
    cyan: mat('cyan light', palette.cyan, { emissive: palette.cyan, emissiveIntensity: 1.6, toneMapped: false }),
    violet: mat('violet light', palette.violet, { emissive: palette.violet, emissiveIntensity: 1.55, toneMapped: false }),
    gold: mat('warm lantern light', palette.gold, { emissive: palette.gold, emissiveIntensity: 1.3, toneMapped: false }),
  };
  const getGeometry = (name, make) => {
    if (!geometries.has(name)) { const g = make(); g.name = name; geometries.set(name, g); }
    return geometries.get(name);
  };
  const box = getGeometry('unit box', () => new T.BoxGeometry(1, 1, 1));
  const cylinder = getGeometry('unit cylinder', () => new T.CylinderGeometry(1, 1, 1, 24));
  const cone = getGeometry('unit canopy', () => new T.ConeGeometry(1, 1, 12));
  const sphere = getGeometry('soft foliage', () => new T.SphereGeometry(1, 12, 9));
  const gem = getGeometry('crystal', () => new T.OctahedronGeometry(1));
  const softbox = getGeometry('soft chamfered box', () => {
    const s = new T.Shape(); const r = 0.09;
    s.moveTo(-.5 + r, -.5); s.lineTo(.5 - r, -.5); s.quadraticCurveTo(.5, -.5, .5, -.5 + r);
    s.lineTo(.5, .5 - r); s.quadraticCurveTo(.5, .5, .5 - r, .5);
    s.lineTo(-.5 + r, .5); s.quadraticCurveTo(-.5, .5, -.5, .5 - r);
    s.lineTo(-.5, -.5 + r); s.quadraticCurveTo(-.5, -.5, -.5 + r, -.5);
    const g = new T.ExtrudeGeometry(s, { depth: .92, bevelEnabled: true, bevelSegments: 2, steps: 1, bevelSize: .035, bevelThickness: .04, curveSegments: 4 });
    g.translate(0, 0, -.46); return g;
  });
  function instance(g, material, p, s = [1, 1, 1], r = [0, 0, 0], castShadow = true) {
    const key = `${g.uuid}:${material.uuid}:${castShadow ? 1 : 0}`;
    let batch = batches.get(key);
    if (!batch) { batch = { geometry: g, material, transforms: [], castShadow }; batches.set(key, batch); }
    batch.transforms.push({ p, s, r });
  }
  const block = (material, x, y, z, w, h, d, ry = 0, soft = false) => instance(soft ? softbox : box, material, [x, y, z], [w, h, d], [0, ry, 0]);
  const disk = (material, x, y, z, radius, h = .15, depthScale = 1) => instance(cylinder, material, [x, y, z], [radius, h, radius * depthScale]);
  function ring(material, x, y, z, radius, thickness = .08, rotation = [-Math.PI / 2, 0, 0], scale = [1, 1, 1]) {
    const g = getGeometry(`torus ${radius} ${thickness}`, () => new T.TorusGeometry(radius, thickness, 6, 64));
    instance(g, material, [x, y, z], scale, rotation, false);
  }
  const leaf = (material, x, y, z, sx, sy = sx, sz = sx) => instance(sphere, material, [x, y, z], [sx, sy, sz]);
  const pillar = (material, x, y, z, radius, h) => instance(cylinder, material, [x, y, z], [radius, h, radius]);
  const archGeometry = (radius, thickness, depth) => getGeometry(`arch ${radius} ${thickness} ${depth}`, () => {
    const shape = new T.Shape(); const inner = radius - thickness;
    shape.absarc(0, 0, radius, 0, Math.PI, false);
    shape.lineTo(-inner, 0); shape.absarc(0, 0, inner, Math.PI, 0, true); shape.closePath();
    const g = new T.ExtrudeGeometry(shape, { depth, bevelEnabled: true, bevelThickness: .06, bevelSize: .04, bevelSegments: 2, steps: 1, curveSegments: 24 });
    g.translate(0, 0, -depth / 2); return g;
  });
  function arch(material, x, y, z, radius, thickness, depth, ry = 0) {
    instance(archGeometry(radius, thickness, depth), material, [x, y, z], [1, 1, 1], [0, ry, 0]);
  }
  function textSign(text, x, y, z, width, height = 0.8, color = '#f7e7ce', bg = null, rotation = 0, subtitle = '') {
    if (typeof document === 'undefined') return;
    const canvas = document.createElement('canvas'); canvas.width = 1024; canvas.height = 160;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    if (bg) { ctx.fillStyle = bg; ctx.fillRect(0, 0, 1024, 160); }
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillStyle = color;
    ctx.font = '500 64px system-ui, sans-serif'; ctx.fillText(text, 512, subtitle ? 60 : 82, 990);
    if (subtitle) { ctx.font = '400 27px system-ui, sans-serif'; ctx.globalAlpha = .75; ctx.fillText(subtitle, 512, 126, 990); }
    const texture = new T.CanvasTexture(canvas); texture.colorSpace = T.SRGBColorSpace; ownedTextures.push(texture);
    const material = new T.MeshBasicMaterial({ map: texture, transparent: true, depthWrite: false, side: T.DoubleSide, toneMapped: false });
    const mesh = new T.Mesh(new T.PlaneGeometry(width, height), material); mesh.position.set(x, y, z); mesh.rotation.y = rotation; group.add(mesh);
    return mesh;
  }
  function path(x1, z1, x2, z2, width = 7) {
    const dx = x2 - x1, dz = z2 - z1, length = Math.hypot(dx, dz), angle = Math.atan2(dx, dz);
    block(m.paving, (x1 + x2) / 2, -.023, (z1 + z2) / 2, width, .08, length, angle);
    const nx = Math.cos(angle), nz = -Math.sin(angle);
    for (const side of [-1, 1]) {
      block(m.ivory, (x1 + x2) / 2 + nx * width * .51 * side, .018, (z1 + z2) / 2 + nz * width * .51 * side, .12, .07, length, angle);
    }
  }

  // A real street section: dark asphalt, raised edges, patched sidewalks and
  // a broad cross street. There is no ceremonial axis or monumental center.
  const asphalt = mat('old violet asphalt', '#353644', { roughness: .93 });
  const paint = mat('faded road paint', '#b9b1aa', { roughness: .96 });
  const yellow = mat('weathered yellow paint', '#b59b6b', { roughness: .9 });
  const tile = mat('small cream tile', '#aaa3a1', { roughness: .8 });
  const red = mat('faded vending red', '#884e62', { roughness: .72 });
  const plaster = mat('pale green plaster', '#788785', { roughness: .86 });
  const blue = mat('dusty blue plaster', '#606b83', { roughness: .86 });
  const brick = mat('aubergine brick', '#614859', { roughness: .92 });
  const neonPink = mat('pink shop neon', '#ed90ca', { emissive: '#ef72bb', emissiveIntensity: 1.1, toneMapped: false });
  block(m.sand, 0, -.23, -6, 156, .38, 166);
  block(asphalt, 0, -.01, -6, 11.5, .08, 139);
  block(asphalt, 0, -.009, 18, 129, .08, 7.5);
  block(asphalt, 0, -.011, -30, 129, .08, 6.5);
  block(asphalt, -29, -.012, -4, 6.8, .08, 117);
  block(asphalt, 29, -.012, -4, 6.8, .08, 117);
  for (const side of [-1, 1]) {
    for (const [centerZ, depth] of [[-28, 96], [53, 22]]) {
      block(m.paving, side * 7, .02, centerZ, 2.45, .14, depth);
      block(m.stone, side * 5.78, .075, centerZ, .13, .25, depth);
    }
    block(m.paving, side * 42, .02, 12.6, 69, .13, 3.2);
    block(m.paving, side * 42, .02, 23.2, 69, .13, 3.2);
    for (let z = -65; z <= 59; z += 2.1) {
      if ((z > 20 && z < 42) || Math.abs(z - 18) < 4 || Math.abs(z + 30) < 3) continue;
      block(m.pale, side * 7, .098, z, 2.35, .015, .023);
      if (z > 22 || z < 10) block(yellow, side * 6.3, .102, z, .32, .015, 1.9);
    }
  }
  for (let z = -63; z < 53; z += 5.5) {
    if (!(z > 20 && z < 42) && Math.abs(z - 18) > 6 && Math.abs(z + 30) > 5) block(paint, 0, .04, z, .11, .015, 2.4);
  }
  // A generous, unprogrammed arrival square. It is ordinary street paving,
  // not a display plinth: level, empty in the middle, and open to every route.
  block(m.paving, 0, 0, 31, 35, .08, 22);
  for (const side of [-1, 1]) block(m.pale, side * 17.35, .045, 31, .07, .016, 21.7);
  for (const z of [20.15, 41.85]) block(m.pale, 0, .045, z, 34.7, .016, .07);
  if(!districtLayout)for (let x = -14; x <= 14; x += 3.5) block(m.pale, x, .043, 31, .023, .012, 21.6);
  if(!districtLayout)for (let z = 23.65; z < 41; z += 3.65) block(m.pale, 0, .043, z, 34.6, .012, .023);
  for (const crossing of [18, -30]) {
    for (let x = -4.7; x <= 4.8; x += .84) block(paint, x, .047, crossing, .47, .014, 2.5);
    for (const z of [crossing - 4.4, crossing + 4.4]) block(paint, 0, .047, z, 9.4, .015, .12);
  }
  for (const [x,z,w,d] of [[-2,-53,2.4,1.7],[2,-7,3.2,1.3],[-3,-36,2.2,2.7],[22,18,2.5,1.8]]) {
    block(m.dark,x,.044,z,w,.012,d,.08);
    for(let i=0;i<5;i++)block(m.copper,x-w*.4+i*w*.2,.056,z,.028,.012,d*.8);
  }
  for(const z of [-45,-13,34]) { disk(m.copper,2.8,.044,z,.57,.02);ring(m.dark,2.8,.06,z,.43,.027); }

  function plant(x,z,y=0,r=.55) {
    pillar(m.rose,x,y+.22,z,r,.44);disk(m.soil,x,y+.45,z,r*.84,.025);
    leaf(m.leaf,x,y+.84,z,r*.8,.68,r*.85);leaf(m.leafLight,x+r*.3,y+1.06,z,r*.55,.42,r*.62);
  }
  function tree(x,z,height=5) {
    block(m.pale,x,.1,z,1.7,.2,1.7);pillar(m.wood,x,height*.36,z,.11,height*.7);
    leaf(m.leaf,x,height*.75,z,height*.27,height*.24,height*.27);
    leaf(m.leafLight,x-.6,height*.8,z+.2,height*.2,height*.2,height*.23);
    leaf(m.leaf,x+.7,height*.65,z-.3,height*.22,height*.2,height*.2);
  }
  function beam(material,a,b,radius=.035) {
    const direction=new T.Vector3(b[0]-a[0],b[1]-a[1],b[2]-a[2]);
    const rotation=new T.Euler().setFromQuaternion(new T.Quaternion().setFromUnitVectors(new T.Vector3(0,1,0),direction.clone().normalize()));
    instance(cylinder,material,[(a[0]+b[0])/2,(a[1]+b[1])/2,(a[2]+b[2])/2],[radius,direction.length(),radius],[rotation.x,rotation.y,rotation.z]);
  }
  function wire(points, radius=.019) {
    const curve = new T.CatmullRomCurve3(points.map(p=>new T.Vector3(...p)));
    const mesh=new T.Mesh(new T.TubeGeometry(curve,24,radius,4,false),m.dark);group.add(mesh);
  }
  function shopSign(label,x,y,z,width,height,color='#e9cba9',bg='#282b36',rotation=0,subtitle='') {
    block(m.dark,x,y,z,width+.18,height+.18,.16,rotation);
    const sx=Math.sin(rotation),sz=Math.cos(rotation);
    textSign(label,x+sx*.09,y,z+sz*.09,width,height,color,bg,rotation,subtitle);
  }
  function verticalSign(word,x,y,z,color,bg='#383247') {
    block(m.dark,x,y,z,1.13,word.length*.85+.42,.35);
    if(typeof document==='undefined')return;
    const c=document.createElement('canvas');c.width=256;c.height=word.length*190+60;
    const ctx=c.getContext('2d');if(!ctx)return;ctx.fillStyle=bg;ctx.fillRect(0,0,c.width,c.height);ctx.fillStyle=color;ctx.textAlign='center';ctx.textBaseline='middle';ctx.font='600 150px sans-serif';
    [...word].forEach((letter,i)=>ctx.fillText(letter,128,125+i*190));
    const t=new T.CanvasTexture(c);t.colorSpace=T.SRGBColorSpace;ownedTextures.push(t);
    const material=new T.MeshBasicMaterial({map:t,toneMapped:false});const sign=new T.Mesh(new T.PlaneGeometry(1,word.length*.85+.22),material);sign.position.set(x,y,z+.19);group.add(sign);
  }
  function roof(x,z,w,d,h,index) {
    block(m.dark,x,h+.09,z,w+.3,.18,d+.3);
    for(const side of [-1,1]) {
      block(m.stone,x+side*(w/2-.1),h+.4,z,.19,.55,d);
      block(m.stone,x,h+.4,z+side*(d/2-.1),w,.55,.19);
    }
    block(m.pale,x-w*.22,h+1.2,z-d*.15,w*.29,2.1,d*.27);
    block(m.dark,x-w*.22,h+2.28,z-d*.15,w*.32,.13,d*.3);
    block(m.copper,x+w*.22,h+.43,z+d*.15,1.6,.73,1.05);
    for(let i=0;i<8;i++)block(m.dark,x+w*.22-.64+i*.18,h+.82,z+d*.15,.065,.06,.84);
    if(index%2===0) {
      const tx=x+w*.2,tz=z-d*.2;
      for(const dx of [-.8,.8])for(const dz of[-.8,.8])pillar(m.dark,tx+dx,h+1,tz+dz,.065,1.8);
      pillar(m.copper,tx,h+2.45,tz,1.08,1.5);disk(m.stone,tx,h+3.26,tz,1.15,.14);
      for(const yy of [h+1.76,h+3.12])ring(m.dark,tx,yy,tz,1.1,.025);
    }
    pillar(m.copper,x-w*.34,h+2.2,z+d*.24,.035,4.2);
    for(let i=0;i<5;i++)block(m.copper,x-w*.34,h+3.5-i*.3,z+d*.24,.7+i*.22,.025,.025);
    if(index%3===0) { plant(x+w*.3,z+d*.32,h+.23,.45);plant(x+w*.39,z,h+.23,.4); }
  }
  function building(x,z,w,d,h,wall,index,label) {
    label = index === 3 ? '24' : ''; // One ordinary corner sign; atmosphere does the storytelling.
    block(wall,x,h/2,z,w,h,d);
    block(m.pale,x,.14,z,w+.3,.28,d+.3);
    const floors=Math.max(2,Math.floor((h-3.5)/2.8));
    for(let floor=0;floor<floors;floor++) {
      const y=4.9+floor*2.8;
      for(let col=0;col<Math.floor(w/2.3);col++) {
        const xx=x-w/2+1.3+col*2.3;const win=(col+floor*3+index)%5<2?m.warmWindow:(col+floor+index)%7===0?m.window:m.glass;
        block(m.dark,xx,y,z+d/2+.04,1.45,1.86,.075);
        block(win,xx,y,z+d/2+.085,1.22,1.63,.04);
        block(m.stone,xx,y,z+d/2+.12,.055,1.64,.035);
        block(m.stone,xx,y-.93,z+d/2+.17,1.58,.08,.31);
        if((col+floor+index)%4===0) {
          block(m.stone,xx+.66,y-.61,z+d/2+.44,.67,.47,.47);
          for(let k=0;k<4;k++)block(m.copper,xx+.66,y-.77+k*.1,z+d/2+.69,.55,.027,.025);
        }
      }
      const side=x<0?1:-1,sx=x+side*w/2;
      for(let col=0;col<Math.floor(d/2.5);col++) {
        const zz=z-d/2+1.35+col*2.5;const win=(col*2+floor+index)%6<2?m.warmWindow:m.glass;
        block(m.dark,sx+side*.045,y,zz,.09,1.8,1.4);block(win,sx+side*.105,y,zz,.035,1.55,1.2);
        block(m.stone,sx+side*.14,y,zz,.04,1.58,.06);
        block(m.stone,sx+side*.18,y-.9,zz,.3,.08,1.5);
      }
      if(floor===0||floor===floors-1)block(m.pale,x,y-1.25,z+d/2+.11,w,.12,.17);
    }
    // Street level storefronts have deep lintels, mullions, translucent panes
    // and ordinary handwritten-scale signs, rather than showroom facades.
    const front=z+d/2;
    block(m.dark,x,1.65,front+.05,w-.6,2.86,.11);
    for(let col=0;col<Math.floor(w/2);col++) {
      const xx=x-w/2+1.08+col*2;
      block(col%3===0?m.glass:m.warmWindow,xx,1.65,front+.13,1.75,2.55,.05);
      block(m.copper,xx+.92,1.68,front+.19,.075,2.8,.12);
      block(m.dark,xx,1.15,front+.22,1.73,.08,.08);
      if(col%2)for(let k=0;k<3;k++)block(k%2?m.rose:m.teal,xx-.46+k*.43,.7,front+.24,.32,.48,.13);
    }
    block(m.dark,x,3.2,front+.48,w+.28,.25,1.18);
    if(index%3===0) {
      for(let col=0;col<Math.floor(w/.7);col++)block(col%2?m.stone:wall,x-w/2+.35+col*.7,3.04,front+.6,.69,.1,1.25,0);
    }
    if(label)shopSign(label,x,3.72,front+.2,Math.min(w-1,8),.78,index%2?'#efd2b0':'#c4dedc','#343141',0,index===0?'COFFEE · RECORDS · OPEN LATE':'');
    const side=x<0?1:-1,sx=x+side*w/2;
    // The reviewed resort facade owns this storefront; retain the building
    // mass and upper floors, but do not run an awning through its entrance.
    const resortEntrance=index===7&&hasResortBuildingEntrance(options.world);
    if(!resortEntrance){
    block(m.dark,sx+side*.06,1.7,z,.11,2.65,d-.8);
    for(let col=0;col<Math.floor(d/2.3);col++) {
      const zz=z-d/2+1.25+col*2.3;
      block(col%3?m.warmWindow:m.glass,sx+side*.13,1.72,zz,.055,2.35,2.02);
      block(m.copper,sx+side*.21,1.7,zz+1.07,.15,2.7,.06);
    }
    block(m.dark,sx+side*.5,3.2,z,1.25,.18,d+.25);
    const angle=side*Math.PI/2;
    if(label)shopSign(label,sx+side*.7,3.65,z,Math.min(6.4,d-1),.83,index%2?'#efb9d2':'#ddcea9','#35303f',angle);
    }
    roof(x,z,w,d,h,index);
  }
  building(-17,4,18,16,13,m.ivory,0,'SIDE B');
  building(17,4,18,16,16,blue,1,'VIDEO');
  building(-16,-18,16,18,18,m.rose,2,'KISSATEN');
  building(16,-18,16,18,15,m.stone,3,'MARU MART');
  building(-14,-42,12,16,12,plaster,4,'COIN LAUNDRY');
  building(16,-42,16,16,20,brick,5,'HOTEL TOKI');
  building(-25,districtLayout?.westBlockMoved?28:32,14,14,10,m.rose,6,'NIGHT BUS');
  building(25,32,14,14,12,plaster,7,'PIXEL');
  building(-37,2,13,18,18,blue,8,'FILM LAB');
  building(38,2,14,18,14,m.ivory,9,'RAMEN');
  building(-38,-24,15,17,14,plaster,10,'RADIO');
  building(38,-24,15,17,19,m.rose,11,'ARCADE');
  building(-40,31,13,13,8,m.stone,12,'NOODLE BAR');
  building(40,32,13,15,10,brick,13,'24 HOURS');
  building(0,53,13,12,8,blue,14,'');

  // Projecting signs, a few handrails and wire-laced roofs carry the skyline.
  // A single modest shop sign leaves the roofs and dusk to carry the scene.
  for(const [x,z,y,len] of [[8.25,-18,6,12],[8.25,-18,9,12],[-8.25,-18,7,11]]) {
    block(m.dark,x,y,z,1.2,.14,len);
    for(const dz of[-len/2,len/2])block(m.copper,x,y+.65,z+dz,1.2,.035,.04);
    const outside=x>0?x-.6:x+.6;
    block(m.copper,outside,y+.93,z,.04,.04,len);
    for(let zz=z-len/2;zz<=z+len/2;zz+=.55)block(m.copper,outside,y+.48,zz,.035,.91,.035);
  }
  for(const side of[-1,1]) {
    for(const z of [23,-4,-31]) {
      const poleX = side * (z === 23 ? 18.3 : 6.9);
      pillar(m.wood,poleX,4.7,z,.12,9.4);
      block(m.dark,poleX,8.73,z,2.5,.12,.16);
      for(const dx of[-.85,0,.85])pillar(m.stone,poleX+dx,8.98,z,.075,.32);
      block(m.dark,poleX,4.8,z,.38,.7,.31);
      const lampX=poleX-side*.8;
      beam(m.copper,[poleX,6.2,z],[lampX,6.6,z],.055);
      block(m.dark,lampX,6.55,z,.72,.13,.42);block(m.gold,lampX,6.47,z,.54,.035,.27);
    }
    for(const dx of[-.85,0,.85])for(const [za,zb] of [[23,-4],[-4,-31]]) {
      const xa=side*(za===23?18.3:6.9)+dx,xb=side*6.9+dx;
      wire([[xa,9,za],[(xa+xb)/2,8.35,(za+zb)/2],[xb,9,zb]]);
    }
  }
  for(const z of[23,-4,-31])for(const offset of[-.23,.23]) {
    const half=z===23?18.3:6.9;
    wire([[-half,8.7,z+offset],[0,7.8,z+offset],[half,8.7,z+offset]],.014);
  }

  function vending(x,z,rotation=0,color=red) {
    const c=Math.cos(rotation),s=Math.sin(rotation),b=(mat,xx,yy,zz,w,h,d)=>block(mat,x+c*xx+s*zz,yy,z-s*xx+c*zz,w,h,d,rotation);
    b(color,0,1.02,0,1.04,2.04,.7);b(m.dark,0,1.29,.363,.83,1.07,.05);
    b(m.warmWindow,-.08,1.45,.398,.63,.75,.035);
    for(let r=0;r<3;r++)for(let k=0;k<4;k++)b((r+k)%2?m.rose:m.teal,-.31+k*.15,1.19+r*.23,.424,.09,.15,.03);
    b(m.dark,0,.46,.39,.72,.2,.05);b(m.gold,.35,1.02,.41,.075,.12,.02);b(m.ivory,0,1.97,.38,.84,.09,.04);
  }
  function bicycle(x,z,rotation=0) {
    const c=Math.cos(rotation),s=Math.sin(rotation),p=(xx,yy,zz=0)=>[x+c*xx+s*zz,yy,z-s*xx+c*zz];
    for(const xx of[-.59,.59]) { ring(m.dark,...p(xx,.43),.37,.035,[0,rotation,0]);ring(m.copper,...p(xx,.43),.3,.017,[0,rotation,0]); }
    for(const [a,b]of[[[-.59,.43],[0,.44]],[[0,.44],[-.23,.99]],[[-.23,.99],[-.59,.43]],[[0,.44],[.41,1.02]],[[.41,1.02],[.59,.43]],[[-.23,.99],[.41,1.02]]])beam(m.teal,p(...a),p(...b),.025);
    block(m.dark,...p(-.25,1.08),.25,.06,.15,rotation);beam(m.copper,p(.41,1.02),p(.39,1.22),.025);beam(m.copper,p(.39,1.22,-.17),p(.39,1.22,.17),.025);
  }
  vending(-7.35,9,Math.PI/2);vending(7.35,1,-Math.PI/2,m.teal);vending(24.1,12.6,0);vending(-25,12.6,0,blue);
  for(const [x,z,a] of [[-7.4,3,.06],[-7.4,1,.09],[17.35,32,Math.PI/2],[21,12.7,.15]]){if(x===17.35&&z===32&&hasResortBuildingEntrance(options.world))continue;bicycle(x,z,a);}
  for(const [x,z]of[[-24,12.9],[-22.7,12.9],[23.4,12.9],[7.5,-11],[-7.5,-22],[-17.4,37],[17.4,28],[-26,27],[26,-20]])plant(x,z);
  for(const [x,z]of[[-29,22],[24,22],[-30,-39],[30,-44],[-46,19],[45,20]])tree(x,z,4.7);
  for(const [x,z]of[[-24,22],[-20.5,22],[-24,24]]) {
    pillar(m.copper,x,.5,z,.06,.92);disk(m.wood,x,.98,z,.62,.08);
    for(const side of[-1,1]){block(m.dark,x+side*.9,.53,z,.42,.08,.45);for(const dz of[-.15,.15])block(m.copper,x+side*.9,.27,z+dz,.045,.5,.035);block(m.wood,x+side*1.07,.94,z,.05,.73,.45);}
  }
  for(const [x,z]of[[-15.5,42.2],[15.5,42.2]]) {
    block(m.wood,x,.65,z,2.5,.13,.7);for(const xx of[-.92,.92])block(m.dark,x+xx,.32,z,.12,.58,.6);block(m.wood,x,1.05,z-.31,2.5,.12,.055);
  }

  // Connected worlds read as ordinary entrances you might have passed before:
  // a station passage, an upstairs arcade, a studio door down the back street.
  function doorway(def) {
    const [x,,z]=def.position,isStudio=def.id==='studio',front=isStudio?-1:1;
    const glow=def.id==='garden'?m.cyan:def.id==='observatory'?neonPink:m.gold;
    const faceAngle=isStudio?Math.PI:0;
    block(m.pale,x,.07,z,5,.14,2.4);
    for(const side of[-1,1]) {
      block(m.stone,x+side*1.96,1.8,z,.55,3.6,1.7);
      block(m.dark,x+side*1.68,1.76,z+front*.87,.12,3.35,.09);
      block(m.copper,x+side*1.57,1.75,z+front*.93,.05,3.35,.035);
    }
    block(m.dark,x,3.53,z,4.65,.28,2.15);
    block(m.stone,x,3.98,z+front*.28,4.55,.68,1.15);
    block(glow,x,3.4,z+front*1.07,3.45,.035,.04);
    // The live portal renderer owns this open rectangle. No painted scene,
    // opaque membrane, destination title or interaction prompt belongs here.
    for(const side of[-1,1]) {
      block(m.copper,x+side*1.37,.87,z+front*.66,.045,1.1,.06);
      beam(m.copper,[x+side*1.37,1.4,z+front*.66],[x+side*1.37,1.4,z-front*.5],.027);
    }
    // Paper notices and tile lines quietly explain where this door goes.
    block(tile,x-2.01,.93,z+front*.88,.36,.47,.015);
    for(let i=0;i<3;i++)block(m.dark,x-2.01,.82+i*.09,z+front*.9,.24,.023,.01);
    for(const side of[-1,1])for(let y=.45;y<3.3;y+=.43)block(m.pale,x+side*1.96,y,z+front*.87,.54,.016,.018);
  }
  const visibleLandmarks=portalDefinitions.filter(def=>!(districtLayout?.studioFrameRemoved&&def.id==='studio'));
  visibleLandmarks.forEach(doorway);

  // Subtle pools of ordinary streetlight; warmth lives at shop level while the
  // sky and distant hills supply the saturated violet dusk outside the streets.
  if(options.lights!==false)for(const [color,intensity,distance,x,y,z]of[
    ['#ffc397',10,15,-17.5,6.4,23],['#ffbf95',10,15,6.1,6.4,-4],['#e898cf',6,12,8,5,8],['#70cfc6',6,10,-15,3.2,14]
  ]){const light=new T.PointLight(color,intensity,distance,2);light.position.set(x,y,z);group.add(light);}

  let instanceCount=0;
  for(const batch of batches.values()) {
    const mesh=new T.InstancedMesh(batch.geometry,batch.material,batch.transforms.length);
    mesh.name=`${batch.material.name} · ${batch.geometry.name}`;mesh.castShadow=batch.castShadow;mesh.receiveShadow=true;
    batch.transforms.forEach(({p,s,r},index)=>{matrixObject.position.set(...p);matrixObject.scale.set(...s);matrixObject.rotation.set(...r);matrixObject.updateMatrix();mesh.setMatrixAt(index,matrixObject.matrix);});
    mesh.instanceMatrix.needsUpdate=true;mesh.computeBoundingSphere();group.add(mesh);instanceCount+=batch.transforms.length;
  }
  group.userData.sceneBudget={batches:batches.size,instances:instanceCount,dynamicObjects:0};
  group.userData.city='valoria';let elapsed=0;
  return {group,colliders:CITY_COLLIDERS.filter(c=>!(districtLayout?.studioFrameRemoved&&c.z===44&&Math.abs(c.x)===1.96&&c.width===.55&&c.depth===1.7&&c.height===3.8)).map(c=>({...c,...districtLayout?.westBlockMoved&&c.x===-25&&c.z===32&&c.width===14&&c.depth===14&&c.height===10?{z:28}:{}})),landmarks:visibleLandmarks.map(p=>({...p,position:[...p.position]})),spawn:[...CITY_SPAWN],
    update(dt=0,time){elapsed=Number.isFinite(time)?time:elapsed+Math.min(.1,Math.max(0,dt));for(const material of shaders)material.uniforms.time.value=elapsed;},
    dispose(){const gs=new Set(),ms=new Set();group.traverse(o=>{if(o.geometry&&!gs.has(o.geometry)){o.geometry.dispose();gs.add(o.geometry);}for(const material of o.material?Array.isArray(o.material)?o.material:[o.material]:[]){if(!ms.has(material)){material.dispose();ms.add(material);}}});ownedTextures.forEach(t=>t.dispose());group.removeFromParent();}
  };
}
